import { Box } from '@cdl/heartbeat-react';
import { useMemo } from 'react';
import { useSelector } from 'react-redux';
import { getDurationFromNumberOfPoints } from '../../client/helpers';
import { ClusterBeat } from '../../clustering.types';
import { ClusterPlotStrip } from './ClusterPlotStrip';
import { Scale } from '../../../plot/types/scale';
import {
  hoveredBeatSelector,
  meanClusterOnsetOffsetDurationSelector,
  pinnedBeatSelector,
} from '../../../beats/beats.selector';

interface Props {
  beats: ClusterBeat[];
  yScale: Scale;
  xScale: Scale;
  height: number;
}
const BEATS_BEFORE_ONSET = 200;

export const ClusterGraph: React.FC<Props> = ({
  beats,
  xScale,
  yScale,
  height,
}) => {
  const hoveredBeat = useSelector(hoveredBeatSelector);
  const pinnedBeat = useSelector(pinnedBeatSelector);
  const meanClusterOnsetOffsetDuration = useSelector(
    meanClusterOnsetOffsetDurationSelector,
  );
  const rectangleStart = xScale(
    getDurationFromNumberOfPoints(BEATS_BEFORE_ONSET + 1),
  );
  const rectangleWidth = xScale(meanClusterOnsetOffsetDuration);

  const clusters = useMemo(
    () =>
      beats.map((beat) => {
        // we take from 200 points before onset and 400
        // after so in the data range it's on the index 400
        // since we took 600 points before onset upon fetching
        const newData = beat.data.slice(400, 800);
        return (
          <ClusterPlotStrip
            key={beat.id}
            beatId={beat.id}
            data={newData}
            xScale={xScale}
            yScale={yScale}
            stroke='black'
          />
        );
      }),
    [beats, xScale, yScale],
  );

  return (
    <g>
      <Box
        as='rect'
        x={rectangleStart}
        width={rectangleWidth}
        height={height}
        fill='primary.500'
        opacity={0.2}
      />
      {clusters}
      {hoveredBeat && (
        <ClusterPlotStrip
          xScale={xScale}
          yScale={yScale}
          stroke='purple.500'
          beatId={hoveredBeat.id}
          data={hoveredBeat.data.slice(400, 800)}
          opacity={1}
        />
      )}
      {pinnedBeat && (
        <ClusterPlotStrip
          xScale={xScale}
          yScale={yScale}
          stroke='yellow.500'
          beatId={pinnedBeat.id}
          data={pinnedBeat.data.slice(400, 800)}
          opacity={1}
        />
      )}
    </g>
  );
};
